import { LoadingSpinner } from "@JohnLewisPartnership/jl-design-system/dist/components/loading-spinner/LoadingSpinner";
import { useCleanBeauty } from "@/hooks/useCleanBeauty/useCleanBeauty";
import { FreeFrom } from "./components/FreeFrom/FreeFrom";
import styles from "./GoodToKnow.module.scss";
import { Badges } from "./components/Badges/Badges";
import { CleanBeautyLabels } from "@/hooks/useCleanBeauty/cleanBeautySchema";
import { FeatureFlags, RuntimeConfig } from "@/types/types";
import { recordImpressionByParam } from "@JohnLewisPartnership/experiments-utils";
import { dispatchMonetateQEvent } from "@/utils/monetate/monetateQ";
import { Experiences } from "@/getServerSideProps/monetate/getExperiences/experiencesSchema";
import { useMonetateId } from "@/hooks/useMonetateId/useMonetateId.ts";
import { transformExperiences } from "@/utils/monetate/transformExperiences";

type GoodToKnowProps = {
  freeFrom: string[];
  badges: CleanBeautyLabels[];
  initialItemsToShow: number;
  goodToKnowObservation: boolean;
};

const GoodToKnow = ({
  freeFrom: freefrom,
  badges,
  initialItemsToShow,
  goodToKnowObservation,
}: GoodToKnowProps) => {
  return (
    <section
      data-testid="good-to-know"
      className={styles.goodToKnowWrapper}
      data-component="GoodToKnow"
    >
      <h2 className={styles.title}>Good to Know</h2>
      <p className={styles.text}>
        Useful information on how this product is good for the environment and
        for you.
      </p>
      <div className={styles.goodToKnow}>
        {badges.length > 0 && (
          <div className={styles.badges}>
            <Badges
              labels={badges}
              goodToKnowObservation={goodToKnowObservation}
            ></Badges>
          </div>
        )}
        {freefrom.length > 0 && (
          <div className={styles.freeform}>
            <FreeFrom
              freeFrom={freefrom}
              initialItemsToShow={initialItemsToShow}
            />
          </div>
        )}
      </div>
      <span className={styles.poweredBy}>
        <span className={styles.disclaimer}>Disclaimer:</span>
        The free from list and conscious beauty labels are provided by Skin
        Match Technology for information purposes only. Formulation and
        ingredients are subject to change by the brand. Please refer to the
        product packaging for the most up-to-date ingredients list and be aware
        that it may change or vary from time to time. For any concerns please
        seek professional advice before use.
      </span>
    </section>
  );
};

type Props = {
  ean: string;
  cleanBeautyBaseUrl: string;
  initialItemsToShow: number;
  flags: FeatureFlags;
  runtimeConfig: RuntimeConfig;
  experiencesRawObject: Experiences;
};

export const GoodToKnowUI = ({
  ean,
  cleanBeautyBaseUrl,
  initialItemsToShow,
  flags,
  runtimeConfig,
  experiencesRawObject,
}: Props) => {
  const { monetateId } = useMonetateId();
  const cleanBeauty = useCleanBeauty(ean, cleanBeautyBaseUrl);
  const showSkinMatch = !!flags.showSkinMatch.value;

  switch (cleanBeauty.status) {
    case "No Action":
    case "Error":
      return null;
    case "Loading":
      return <LoadingSpinner />;
    case "Done": {
      const { free_From: cleanBeautyFreefrom, labels: badges } =
        cleanBeauty.data || { free_From: [], labels: [] };
      if (cleanBeautyFreefrom.length === 0 && badges.length === 0) {
        return null;
      }
      if (flags.goodToKnowObservation.value === true) {
        recordImpressionByParam(
          runtimeConfig?.monetateEnvironment,
          transformExperiences(experiencesRawObject),
          "pdp-scaffold-goodToKnowObservation",
          "true",
          monetateId,
        );
      }
      recordImpressionByParam(
        runtimeConfig?.monetateEnvironment,
        transformExperiences(experiencesRawObject),
        "pdp-scaffold-showSkinMatch",
        showSkinMatch ? "true" : "false",
        monetateId,
      );
      dispatchMonetateQEvent("wpdp-good-to-know-content-found");

      return showSkinMatch ? (
        <GoodToKnow
          freeFrom={cleanBeautyFreefrom}
          badges={badges}
          initialItemsToShow={initialItemsToShow}
          goodToKnowObservation={flags.goodToKnowObservation.value === true}
        ></GoodToKnow>
      ) : null;
    }
    default: {
      return null;
    }
  }
};
